import {Injectable, isDevMode} from '@angular/core';
import {environment} from "../../../environments/environment";
import {AngularFirestore, AngularFirestoreCollection, DocumentReference} from "@angular/fire/compat/firestore";
import {combineLatest, filter, from, Observable} from "rxjs";
import {Task,} from "./models/task.model";
import {Project} from "./models/project.model";
import {Message} from "./interfaces/comments.interface";
import firebase from "firebase/compat/app";
import {TaskEmployeeModel} from "./models/taskemployee.model";
import {catchError, finalize, map} from "rxjs/operators";
import {FileUploadTask} from "./task-file-upload/file-upload-task.model";
import {AngularFireStorage} from "@angular/fire/compat/storage";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import UploadTaskSnapshot = firebase.storage.UploadTaskSnapshot;
import User = firebase.User;
import {NotificationModel, NotificationService} from "../../services/notification.service";
import {EmployeeService} from "../../services/employee.service";


export interface TaskUser {
    email: string;
    userName: string;
}

interface TriggerResult {
    assignees: TaskUser[];
    collaborators: TaskUser[];
}


@Injectable({
    providedIn: 'root'
})
export class TasksService {
  env = isDevMode() ? environment.testMode : environment.productionMode;
  private tasksCollection: AngularFirestoreCollection<Task>;
  private projectCollection: AngularFirestoreCollection<Project>
  basePath = 'tasksDocuments'
  currentUser!: User | null;
  userName = '';
  currentUserEmail = '';

  constructor(private firestore: AngularFirestore,
              private fb: FormBuilder,
              private mNotifications: NotificationService,
              private employeeService: EmployeeService,
              private storage: AngularFireStorage
              // Consider injecting an AuthService here
  ) {
    this.employeeService.initUserCache();
    this.tasksCollection = this.firestore.collection<Task>(this.env.tasks);
    this.projectCollection = this.firestore.collection<Project>(this.env.projects);

    try {
      const userDataString = localStorage.getItem('userData');
      if (userDataString) {
        const userData = JSON.parse(userDataString); // Add type if available: JSON.parse(userDataString) as UserDataInterface;
        this.userName = userData?.userName;
        this.currentUserEmail = userData?.email;
      } else {
        console.warn('userData not found in localStorage');
        // Handle missing userData, e.g., set default or redirect
      }

      const userString = localStorage.getItem('user');
      if (userString) {
        this.currentUser = JSON.parse(userString) as User; // Cast to firebase.User
      } else {
        console.warn('user not found in localStorage');
        // Handle missing user, e.g., redirect to login
      }
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
      // Handle parsing errors, e.g., clear localStorage or redirect
    }
  }

  getTasks(): Observable<Task[]> {

    return this.tasksCollection.valueChanges({idField: 'id'});
  }

  getProjects(): Observable<Project[]> {
    return this.projectCollection.valueChanges({idField: 'id'});
  }

  updateCollaborators(data: TaskEmployeeModel[], taskId: string) {
    const collaboratorsField = firebase.firestore.FieldValue.arrayUnion(...data);
    return this.firestore.collection(this.env.tasks).doc(taskId).update({
      collaborators: data
    })
  }


  addTask(task: Task) {
    return this.tasksCollection.add(task).then(res => {
      this.tasksCollection.doc(res.id).update({id: res.id})
    });

  }

  addTaskWithNotification(task: Task, config?: Partial<NotificationModel>) {
    return this.tasksCollection.add(task).then(res => {
      this.tasksCollection.doc(res.id).update({id: res.id})
      // Only send notification once, either with config or without
      if (config) {
        this.mNotifications.createNotificationFromTask(task, config);
      } else {
        this.mNotifications.createNotificationFromTask(task);
      }
    });

  }

  addProject(project: Project): Promise<DocumentReference<Project>> {
    return this.projectCollection.add(project);
  }

  updateProject(project: Project): void {
    const projectDoc = this.projectCollection.doc(project.id);
    projectDoc.update(project);
  }

  async updateTask(task: Task) {
    const taskDoc = this.tasksCollection.doc(task.id);
    return await taskDoc.update(task);
  }

  deleteTask(task: Task) {
    const taskDoc = this.tasksCollection.doc(task.id);
    return taskDoc.delete()
  }

  deleteProject(project: Project): void {
    const projectDoc = this.projectCollection.doc(project.id);
    projectDoc.delete().then(r => {

    });
  }

  getCommentsFromTask(id: string) {
    return this.firestore.collection(this.env.tasks).doc(id).collection<Message>('comments').valueChanges({idField: 'id'});
  }

  addComment(id: string, comment: Message) {
    return this.firestore.collection(this.env.tasks).doc(id).collection<Message>('comments').add(comment)
  }

  getColaboratorsFromTask(taskId: string) {
    return this.firestore.collection<Task>(this.env.tasks).doc(taskId).valueChanges();

  }


  fetchTasksByProjectId(projectId: string): Observable<Task[]> {
    if (projectId === "All" || projectId === "My Tasks") {
      return this.firestore.collection<Task>(this.env.tasks).valueChanges();
    }

    // No need to create a separate query variable
    return from(
      this.firestore.collection<Task>(this.env.tasks, (ref) =>
        ref.where('project.id', '==', projectId)
      ).valueChanges()
    ) as Observable<Task[]>;
  }

  // getMyTasks(userName: string): Observable<Task[]> {
  //   return this.firestore.collection<Task>(this.env.tasks, (ref) =>
  //     ref.where('taskFor', 'array-contains', userName)
  //       .where('taskByName', '==', userName)
  //   ).valueChanges();
  // }
  getMyTasks(userEmail: string): Observable<Task[]> {
    const tasksByAssignees$ = this.firestore.collection<Task>(this.env.tasks, (ref) =>
      ref.where('assignees', 'array-contains', {
        email: userEmail,
        userName: this.getUserNameForEmail(userEmail) || this.userName
      }).orderBy('due_date','desc')
    ).valueChanges();

    const tasksByTaskBy$ = this.firestore.collection<Task>(this.env.tasks, (ref) =>
      ref.where('taskBy.email', '==', userEmail)
    ).valueChanges();

    const tasksByCollaborators$ = this.firestore.collection<Task>(this.env.tasks, (ref) =>
      ref.where('collaborators', 'array-contains', {
        email: userEmail,
        userName: this.getUserNameForEmail(userEmail) || this.userName
      })
    ).valueChanges();

    return combineLatest([tasksByAssignees$, tasksByTaskBy$, tasksByCollaborators$]).pipe(
      map(([tasksByAssignees, tasksByTaskBy, tasksByCollaborators]) => {
        const allTasks = [...tasksByAssignees, ...tasksByTaskBy, ...tasksByCollaborators];
        return this.removeDuplicateTasks(allTasks);
      })
    );
  }

// Helper function to remove duplicate tasks based on their ID
  removeDuplicateTasks(tasks: Task[]): Task[] {
    const uniqueTasks = new Map<string, Task>();
    tasks.forEach(task => uniqueTasks.set(task.id!, task));
    return Array.from(uniqueTasks.values());
  }

  pushFileToStorage(fileUpload: FileUploadTask, taskId: string): Observable<UploadTaskSnapshot | undefined> {
    const filePath = `${this.basePath}/${fileUpload.file.name}`;
    const storageRef = this.storage.ref(filePath);
    const uploadTask = this.storage.upload(filePath, fileUpload.file);

    uploadTask.snapshotChanges().pipe(
      finalize(() => {
        storageRef.getDownloadURL().subscribe({
          next: (downloadURL: string) => {
            fileUpload.url = downloadURL;
            fileUpload.name = fileUpload.file.name;
            this.saveFileData(fileUpload, taskId);
          },
          error: (error) => {
            console.error("Error getting download URL:", error);
            // Handle the error, e.g., display an error message to the user
          }
        });
      }),
      catchError(error => {
        console.error("Error uploading file:", error);
        // Handle the error, e.g., display an error message to the user
        throw error; // Re-throw the error to propagate it to the subscriber
      })
    )
      .subscribe();
    return uploadTask.snapshotChanges()
  }

  private saveFileData(fileUpload: FileUploadTask, taskId: string) {
    const attachment = {
      fileName: fileUpload.name,
      uploadedAt: new Date(),
      uploadedBy: fileUpload.uploadedBy,
      status: 'uploaded',
      uploadedById: fileUpload.uploadedById, // Use currentUser's ID
      // fileUrl: new FormControl(file.webkitRelativePath),
      url: fileUpload.url,
    }
    this.firestore.doc(`tasks/${taskId}`).set({
      attachments: firebase.firestore.FieldValue.arrayUnion(attachment)
    }, {merge: true}).then(_ => {
      console.log(`Added Attachment in ${taskId}`)
    }).catch(err => {
        console.log(`Error adding attachment in ${taskId} ERROR :: ${err}`)
      }
    )


  }

  createTaskForm(data?: Task): FormGroup {
// alert(this.currentUser?.uid)
    return this.fb.group({
      id: [data?.id || this.getRandomID()],
      img: [data?.img || 'assets/user1.png'],
      projectName: [data?.projectName || null],
      projectId: [data?.projectId || null],
      taskByName: [this.currentUser?.email || '', Validators.required],
      taskById: [this.currentUser?.uid || '', Validators.required],
      assignees: [data?.assignees || null],
      title: [data?.title || null, Validators.required],
      done: [data?.done || null],
      project: [null],
      subTasks: this.fb.array([]),
      attachments: this.fb.array([]),
      priority: [data?.priority || null],
      due_date: [data?.due_date || null],
      note: [data?.note, Validators.required],
    });
// if(this.isNewEvent)this.taskForm.get('taskById')?.setValue(this.currentUser?.uid || '')
//     if(this.isNewEvent)this.taskForm.get('taskByName')?.setValue(this.currentUser?.email || '')
  }

  private getRandomID(): number {
    const S4 = () => {
      return ((1 + Math.random()) * 0x10000) | 0;
    };
    return S4() + S4();
  }

  addAutomatedTask(data: Partial<Task>, config?: Partial<NotificationModel>) {

    let mTaskForm = this.createTaskForm(data as Task);
    // Remove the separate notification call, let addTaskWithNotification handle it
    return this.addTaskWithNotification(mTaskForm.value, config);

  }

  //this function will give collaborators and emails for the task according to heading and centreCode


// Update only the trigger methods for now
  getTaskForAndCollaboratorsCounsellingTrigger(centreCode: string, heading?: string): TriggerResult {
    const assignees: TaskUser[] = [];
    const collaborators: TaskUser[] = [];

    const emailsToAdd: string[] = ['gauravdahale@icloud.com' ]; // Added default assignees
    // const collaboratorsToAdd: string[] = []; // Commented out for now

    switch (centreCode) {
      case 'ALAhmedabad':
        emailsToAdd.push('counsellor.ahmedabad@accesslife.org');
        // collaboratorsToAdd.push('manager.ahmedabad@accesslife.org');
        break;
      case 'ALBangalore':
        emailsToAdd.push('counsellor.bengaluru@accesslife.org');
        // collaboratorsToAdd.push('manager.bengaluru@accesslife.org');
        break;
      case 'ALCoimbatore':
        emailsToAdd.push('counsellor.coimbatore@accesslife.org');
        // collaboratorsToAdd.push('manager.coimbatore@accesslife.org');
        break;
      case 'ALChandigarh':
        emailsToAdd.push('srcounsellor@accesslife.org');
        // collaboratorsToAdd.push('manager.chandigarh@accesslife.org');
        break;
      case 'ALManipal':
        emailsToAdd.push('counsellor.bengaluru@accesslife.org');
        // collaboratorsToAdd.push('manager.manipal@accesslife.org');
        break;
      case 'ALPune':
        emailsToAdd.push('counsellor.pune@accesslife.org');
        // collaboratorsToAdd.push('manager.pune@accesslife.org');
        break;
      case 'ALBorivali':
        emailsToAdd.push('counsellor1.mumbai@accesslife.org');
        // collaboratorsToAdd.push('manager.borivali@accesslife.org');
        break;
      case 'ALDiamondGarden':
        emailsToAdd.push('counsellor3.mumbai@accesslife.org');
        // collaboratorsToAdd.push('manager.diamondgarden@accesslife.org');
        break;
      case 'ALGurunanak':
        emailsToAdd.push('counsellor2.mumbai@accesslife.org');
        // collaboratorsToAdd.push('manager.gurunanak@accesslife.org');
        break;
      case 'ALSanduGarden':
        emailsToAdd.push('counsellor3.mumbai@accesslife.org', 'counsellor1.mumbai@accesslife.org');
        // collaboratorsToAdd.push('manager.sandugarden@accesslife.org');
        break;
    }

    emailsToAdd.forEach(email => {
      const userName = this.getUserNameForEmail(email);
      assignees.push({email, userName: userName || email});
    });

    return {assignees, collaborators};
  }


  getTaskForAndCollaboratorsEducationTrigger(centreCode: string, heading?: string): TriggerResult {
    const assignees: TaskUser[] = [];
    const collaborators: TaskUser[] = [];

    const emailsToAdd: string[] = ['gauravdahale@icloud.com']; // Added default assignees    const collaboratorsToAdd: string[] = []; // Commented out for now
    const collaboratorsToAdd: string[] = []; // Added default assignees    const collaboratorsToAdd: string[] = []; // Commented out for now

    switch (centreCode) {
      case 'ALAhmedabad':
        emailsToAdd.push('teacher.ahmedabad@accesslife.org');
       if (heading=='Report Card'){
         collaboratorsToAdd.push('ldteacher@accesslife.org',
         'sr.teacher@accesslife.org',
         'gmpdo@accesslife.org')
       }

        break;
      case 'ALBangalore':
        emailsToAdd.push('teacher.bengaluru@accesslife.org');

        break;
      case 'ALCoimbatore':
        emailsToAdd.push('teacher.coimbatore@accesslife.org');
        break;
      case 'ALChandigarh':
        emailsToAdd.push('teacher.chandigarh@accesslife.org');
        break;
      case 'ALManipal':
        emailsToAdd.push('teacher.manipal@accesslife.org');
        break;
      case 'ALPune':
        emailsToAdd.push('teacher.pune@accesslife.org');
        break;
      case 'ALBorivali':
        emailsToAdd.push('teacher.mumbai1@accesslife.org','teacher.mumbai@accesslife.org');
        break;
      case 'ALDiamondGarden':
        emailsToAdd.push('teacher.mumbai1@accesslife.org','teacher.mumbai@accesslife.org','srteacher@accesslife.org');
        break;
      case 'ALGurunanak':
        emailsToAdd.push('teacher.mumbai@accesslife.org','teacher.mumbai1@accesslife.org');
        break;
      case 'ALSanduGarden':
        emailsToAdd.push('teacher.mumbai1@accesslife.org','teacher.mumbai@accesslife.org');
        break;
    }
    if (heading=='Report Card'){
      collaboratorsToAdd.push('ldteacher@accesslife.org',
        'srteacher@accesslife.org',
        'gmpdo@accesslife.org')
    }
    emailsToAdd.forEach(email => {
      const userName = this.getUserNameForEmail(email);
      assignees.push({email, userName: userName || email});
    });
    // collaboratorsToAdd.forEach(email => {
    //   const userName = this.getUserNameForEmail(email);
    //   collaborators.push({email, userName: userName || 'NA'});
    // });

    return {assignees, collaborators};
  }

  counsellingAssessementTrigger(centreCode: string, heading: string) {
    let triggerFor = this.getTaskForAndCollaboratorsCounsellingTrigger(centreCode, heading);
    return triggerFor;
  }

  getUserNameForEmail(email: string): string | undefined {
    // Try cache first
    const cachedName = this.employeeService.getCachedUserName(email);
    if (cachedName) {
      return cachedName; // Returns string
    }

    // Fallback to direct query. This subscription is for side effects (e.g., populating cache).
    // Its outcome does not affect the synchronous return value of this specific call.
    this.employeeService.getUserNameByEmail(email).subscribe({
      next: (userNameValue) => {
        if (userNameValue) {
          const name = userNameValue.toString();
          // You might want to use 'name' here for a side effect,
          // e.g., explicitly updating the cache if getCachedUserName doesn't automatically
          // benefit from this stream for future synchronous calls.
          // console.log(`Fetched user name for ${email}: ${name} (for cache/log)`);
        }
        // This callback now implicitly returns undefined (void), which is fine for a subscriber
        // primarily concerned with side effects.
      },
      error: (err) => {
        console.error(`Error fetching user name for ${email} via subscription:`, err);
      }
    });

    return undefined; // Returns undefined if not found in cache synchronously
  }











}
